import { collection, doc, getDoc, getDocs, query, where, runTransaction, serverTimestamp, Timestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { ExportIdentity, ExportIdentityCorrection, exportRaceRows, ExportResultRow, resultExportData, resolveExportIdentity, validateIdentityCorrection } from '../utils/kutcResultExport';
import { DuvOptionalDetails, resolveDuvDetails } from '../utils/kutcDuvDetails';
import type { KUTCResultEntry } from './kutcResultsService';

export interface ExportRace {
  distanceKey: string;
  name: string;
  rows: ExportResultRow[];
  sourceResults?: KUTCResultEntry[];
}

export interface ResultExportPreview {
  editionId: string;
  resultsStatus: string;
  races: ExportRace[];
  identities: Record<string, ExportIdentity>;
  identityTargets: Record<string, IdentitySaveTargets>;
  optionalDetails?: Record<string, DuvOptionalDetails>;
}

interface IdentityTarget {
  id: string;
  identity: ExportIdentity;
}

export interface IdentitySaveTargets {
  profiles: IdentityTarget[];
  registrations: IdentityTarget[];
  error?: string;
}

const identityFields = (data: ExportIdentity): ExportIdentity => ({
  gender: data.gender ?? null, dateOfBirth: data.dateOfBirth ?? null, nationality: data.nationality ?? null
});

const identityVersion = (value: ExportIdentity): string => JSON.stringify(identityFields(value), (_key, field) => {
  if (field && typeof field.toMillis === 'function') return { timestamp: field.toMillis() };
  return field;
});

/** Read exactly one edition. No collection-group queries or cross-year fallback. */
export async function loadKutcResultExport(editionId: string): Promise<ResultExportPreview> {
  if (!/^kutc-\d{4}$/.test(editionId)) throw new Error('Velg en KUTC-årsutgave.');
  const edition = await getDoc(doc(db, 'eventEditions', editionId));
  if (!edition.exists()) throw new Error('Fant ikke valgt årsutgave.');
  const raceDocs = await getDocs(collection(db, 'kutcResults', editionId, 'races'));
  // Race documents may be empty parents; metadata is the authoritative distance list.
  const root = await getDoc(doc(db, 'kutcResults', editionId));
  const summaryDoc = await getDoc(doc(db, 'kutcResults', editionId, 'metadata', 'summary'));
  const rootData = root.data();
  const metadata = rootData?.metadata || rootData?.summary || summaryDoc.data();
  const names = new Map<string, string>();
  for (const race of metadata?.races || []) {
    if (race.distanceKey && race.distanceKey !== 'total') names.set(race.distanceKey, race.raceName || race.distanceKey);
  }
  raceDocs.docs.forEach(race => {
    if (race.id !== 'total') names.set(race.id, names.get(race.id) || race.data().raceName || race.id);
  });
  const rawRaces = await Promise.all(Array.from(names.entries()).map(async ([distanceKey, name]) => {
    if (distanceKey.includes('/') || distanceKey.toLowerCase() === 'total') throw new Error('Ugyldig distanse i resultatmetadata.');
    // Do not orderBy: Firestore would silently exclude results lacking raceRank.
    const snapshot = await getDocs(collection(db, 'kutcResults', editionId, 'races', distanceKey, 'results'));
    const results = snapshot.docs.map(result => {
      const data = result.data();
      if (data.personId != null && Number(data.personId) !== Number(result.id)) throw new Error('Resultat med motstridende person-ID.');
      return { ...data, personId: Number(result.id) } as KUTCResultEntry;
    });
    return { distanceKey, name, results };
  }));
  const identities: Record<string, ExportIdentity> = {};
  const optionalDetails: Record<string, DuvOptionalDetails> = {};
  const identityTargets: Record<string, IdentitySaveTargets> = {};
  const identityLinkErrors: Record<string, string> = {};
  const registrations = await getDocs(query(collection(db, 'registrations'), where('editionId', '==', editionId)));
  const personIds = Array.from(new Set(rawRaces.flatMap(race => race.results.map(row => row.personId))));
  await Promise.all(personIds.filter(id => Number.isInteger(id) && id > 0).map(async personId => {
    // Exact identity link only. Never match participants by name or a reused bib.
    const profiles = await getDocs(query(collection(db, 'users'), where('personId', 'in', [personId, String(personId)])));
    // Numeric legacy IDs can collide with another person in RunnersHub. Names
    // are a consistency check, never a way to select a different profile.
    const nameKey = (first: unknown, last: unknown) => `${first || ''} ${last || ''}`.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
    const expectedNames = new Set(rawRaces.flatMap(race => race.results.filter(result => result.personId === personId).map(result => nameKey(result.firstName, result.lastName))));
    const mismatch = profiles.docs.some(profile => !expectedNames.has(nameKey(profile.data().firstName, profile.data().lastName)));
    if (mismatch) identityLinkErrors[personId] = 'Person-ID peker på en annen profil enn navnet i resultatet. Koblingen må rettes før eksport eller profillagring.';
    const linkedUserIds = new Set(profiles.docs.flatMap(profile => [profile.id, profile.data().uid, profile.data().userId].filter(Boolean)));
    const matchingRegistrations = registrations.docs.filter(document => {
      const registration = document.data();
      return registration.editionId === editionId && (
        registration.personId != null ? Number(registration.personId) === personId : linkedUserIds.has(registration.userId)
      )
    });
    optionalDetails[String(personId)] = resolveDuvDetails(profiles.docs.map(profile => profile.data()), matchingRegistrations.map(registration => registration.data()));
    identities[String(personId)] = resolveExportIdentity(profiles.docs.map(profile => profile.data()), matchingRegistrations.map(registration => registration.data()));
    identityTargets[String(personId)] = {
      ...(mismatch ? { error: identityLinkErrors[personId] } : {}),
      profiles: profiles.docs.map(profile => ({ id: profile.id, identity: identityFields(profile.data()) })),
      registrations: matchingRegistrations.map(registration => ({ id: registration.id, identity: identityFields(registration.data()) }))
    };
  }));
  return {
    editionId,
    resultsStatus: String(edition.data().resultsStatus || 'unknown'),
    identities, identityTargets, optionalDetails,
    races: rawRaces.map(race => ({ distanceKey: race.distanceKey, name: race.name, sourceResults: race.results, rows: exportRaceRows(race.results, identities).map(row =>
      identityLinkErrors[row.personId] ? { ...row, issues: [...row.issues, identityLinkErrors[row.personId]] } : row
    ) }))
      .sort((a, b) => a.distanceKey.localeCompare(b.distanceKey, undefined, { numeric: true }))
  };
}

/** Explicit admin action, atomic with a private before/after audit record.
 * Existing Firestore rules protect users/registrations/adminContent writes. */
export async function saveKutcExportIdentity(
  editionId: string, personId: number, input: ExportIdentityCorrection, targets: IdentitySaveTargets
): Promise<IdentitySaveTargets> {
  const value = validateIdentityCorrection(input);
  const actor = auth.currentUser;
  if (!actor) throw new Error('Du må være innlogget som administrator.');
  if (targets.error) throw new Error(targets.error);
  if (!/^kutc-\d{4}$/.test(editionId) || !Number.isInteger(personId) || personId <= 0 || !targets.profiles.length) {
    throw new Error('Personen mangler en entydig profilkobling. Bruk bare denne eksporten eller rett profilkoblingen først.');
  }
  const allTargets = [
    ...targets.profiles.map(target => ({ ...target, kind: 'users' })),
    ...targets.registrations.map(target => ({ ...target, kind: 'registrations' }))
  ];
  if (allTargets.some(target => !target.id || target.id.includes('/')) || allTargets.length > 450) throw new Error('Ugyldige lagringsmål.');
  const auditRef = doc(collection(db, 'adminContent'));
  const payload = {
    gender: value.gender === 'M' ? 'male' : 'female',
    dateOfBirth: Timestamp.fromDate(new Date(`${value.birthdate}T00:00:00Z`)),
    yearOfBirth: Number(value.birthdate.slice(0, 4)), nationality: value.nationality,
    updatedAt: serverTimestamp()
  };
  await runTransaction(db, async transaction => {
    const admin = await transaction.get(doc(db, 'admins', actor.uid));
    if (!admin.exists()) throw new Error('Kun administratorer kan lagre profilrettelser.');
    const references = allTargets.map(target => doc(db, target.kind, target.id));
    const snapshots = await Promise.all(references.map(reference => transaction.get(reference)));
    const publicRef = doc(db, 'publicProfiles', String(personId));
    const publicSnapshot = await transaction.get(publicRef);
    const profileData = snapshots.slice(0, targets.profiles.length).map(snapshot => snapshot.data());
    const linkedIds = new Set(profileData.flatMap((profile, index) => [targets.profiles[index].id, profile?.uid, profile?.userId].filter(Boolean)));
    snapshots.forEach((snapshot, index) => {
      const target = allTargets[index];
      const data = snapshot.data();
      if (!snapshot.exists() || !data || identityVersion(data) !== identityVersion(target.identity)) {
        throw new Error('Opplysninger er endret siden innlasting. Hent oppdaterte resultater før du lagrer.');
      }
      const linked = target.kind === 'users' ? Number(data.personId) === personId
        : data.editionId === editionId && (data.personId != null ? Number(data.personId) === personId : linkedIds.has(data.userId));
      if (!linked) throw new Error('Profilkoblingen er endret. Hent oppdaterte resultater før du lagrer.');
    });
    if (publicSnapshot.exists() && publicSnapshot.data().personId != null && Number(publicSnapshot.data().personId) !== personId) {
      throw new Error('Offentlig profil har motstridende person-ID.');
    }
    references.forEach(reference => transaction.update(reference, payload));
    // Do not publish a new full birthday or touch names/consents/payment fields.
    if (publicSnapshot.exists()) transaction.update(publicRef, {
      gender: payload.gender, nationality: payload.nationality, yearOfBirth: payload.yearOfBirth, updatedAt: serverTimestamp()
    });
    transaction.set(auditRef, {
      type: 'kutc_export_identity_correction', editionId, personId, actorUid: actor.uid, createdAt: serverTimestamp(),
      changes: allTargets.map((target, index) => ({ collection: target.kind, documentId: target.id,
        before: { ...identityFields(snapshots[index].data() || {}), yearOfBirth: snapshots[index].data()?.yearOfBirth ?? null },
        after: { gender: payload.gender, dateOfBirth: payload.dateOfBirth, yearOfBirth: payload.yearOfBirth, nationality: payload.nationality } }))
    });
  });
  const savedIdentity = identityFields(payload);
  return {
    profiles: targets.profiles.map(target => ({ ...target, identity: savedIdentity })),
    registrations: targets.registrations.map(target => ({ ...target, identity: savedIdentity }))
  };
}

export async function downloadKutcResults(editionId: string, race: ExportRace, rows: ExportResultRow[]): Promise<void> {
  const data = resultExportData(editionId, race.distanceKey, rows);
  const { default: writeExcelFile } = await import('write-excel-file/browser');
  await writeExcelFile(data, {
    sheet: 'Results',
    columns: [12, 14, 24, 24, 16, 10, 14, 14].map(width => ({ width }))
  }).toFile(`${editionId}_${race.distanceKey.replace(/[^a-zA-Z0-9_-]/g, '-')}_UTMB.xlsx`);
}
